import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../public-theme.css';
import '../../public-theme-compat.css';
import '../../public-design-system.css';
import '../../cms/siteCmsPublicIsolation.css';
import '../../cms/siteCmsMobileOverrides.css';
import '../../public-motion.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NON_MARKETING_PATHS = ['/login', '/kiosk', '/launcher', '/staff', '/messages', '/analytics', '/bookings', '/settings', '/admin', '/consent'];
const isMarketingPath = pathname => !NON_MARKETING_PATHS.some(path => pathname === path || pathname.startsWith(`${path}/`));

const PublicSiteBoundary = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [motion, setMotion] = useState('smooth');
  const marketing = isMarketingPath(location.pathname);

  useEffect(() => {
    if (!marketing || !BACKEND_URL) return undefined;
    let active = true;
    fetch(`${BACKEND_URL}/api/v2/public/site-style`)
      .then(response => response.ok ? response.json() : null)
      .then(data => {
        if (!active) return;
        const next = data?.style?.motion;
        if (['quiet', 'smooth', 'dynamic'].includes(next)) setMotion(next);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [marketing]);

  useEffect(() => {
    if (!marketing || !location.hash) return undefined;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let cancelled = false;
    let attempts = 0;
    let timer;

    const scrollToHash = () => {
      if (cancelled || !location.hash) return;
      const id = decodeURIComponent(location.hash.slice(1));
      const target = document.getElementById(id);
      if (target) {
        requestAnimationFrame(() => target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' }));
        return;
      }
      attempts += 1;
      if (attempts < 50) timer = window.setTimeout(scrollToHash, 60);
    };

    timer = window.setTimeout(scrollToHash, 40);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [location.pathname, location.search, location.hash, marketing]);

  useEffect(() => {
    if (!marketing) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;

    const onClick = event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || !root.contains(anchor) || anchor.hasAttribute('download')) return;
      if (anchor.target && anchor.target !== '_self') return;

      const rawHref = anchor.getAttribute('href') || '';
      if (!rawHref || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:') || rawHref.startsWith('javascript:')) return;

      let url;
      try { url = new URL(anchor.href, window.location.href); } catch { return; }
      if (url.origin !== window.location.origin) return;

      const samePage = url.pathname === location.pathname && url.search === location.search;
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      if (samePage) {
        event.preventDefault();
        if (url.hash) {
const next = `${url.pathname}${url.search}${url.hash}`;
if (`${location.pathname}${location.search}${location.hash}` === next) {
  const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
  target?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
} else {
  navigate(next);
}
        } else {
window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
        }
        return;
      }

      if (!isMarketingPath(url.pathname)) return;
      event.preventDefault();
      navigate(`${url.pathname}${url.search}${url.hash}`);
    };

    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [location.pathname, location.search, location.hash, marketing, navigate]);

  return (
    <div ref={rootRef} className="public-site" data-public-marketing={marketing ? 'true' : 'false'} data-public-motion={motion}>
      <div className="public-motion-page">
        {children}
      </div>
    </div>
  );
};

export default PublicSiteBoundary;
